import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import {
  emitSearchFormCtaAnalytics,
  UpdateLocation,
} from '@rover/react-lib/src/pages/landing/utilities/analyticsEvents';
import { Location } from '@rover/react-lib/src/pages/search/SearchPage/components';
import { LocationOnChangeData } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Location';
import { Skills } from '@rover/react-lib/src/pages/training/constants/skillsAndBehaviors';
import {
  getTrainerSearchPageUrl,
  parseCoordinate,
} from '@rover/react-lib/src/pages/training/utils/params';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

export type DogTrainingLandingSearchFormFilters = Partial<
  Pick<
    SearchFilters,
    | 'centerlat'
    | 'centerlng'
    | 'location'
    | 'locationAccuracy'
    | 'locationType'
    | 'rawLocationTypes'
  >
> & { skills?: string[] };

export interface DogTrainingLandingSearchFormProps {
  puppy?: boolean;
  showSearchFilters?: boolean;
}

const initialSearchFilters: DogTrainingLandingSearchFormFilters = {
  location: undefined,
  centerlat: undefined,
  centerlng: undefined,
  locationType: undefined,
  locationAccuracy: undefined,
  rawLocationTypes: undefined,
};
const initialPuppySearchFilters: DogTrainingLandingSearchFormFilters = {
  ...initialSearchFilters,
  skills: [Skills.PUPPY_TRAINING],
};
const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[ServiceLandingEnum.DOG_TRAINING];

const DogTrainingLandingSearchForm: React.FC<
  DogTrainingLandingSearchFormProps & { upliftedContent?: boolean }
> = ({ puppy, showSearchFilters = true, upliftedContent = false }) => {
  const theme = useTheme();

  const [searchFilters, setSearchFilters] = useState<DogTrainingLandingSearchFormFilters>(
    puppy ? { ...initialPuppySearchFilters } : { ...initialSearchFilters }
  );

  const onLocationClear = useCallback(() => {
    const newFilters = {
      ...searchFilters,
      ...(puppy ? initialPuppySearchFilters : initialSearchFilters),
    };
    setSearchFilters(newFilters);
  }, [searchFilters, puppy]);

  const onLocationChange = useCallback(
    (newPartialFilters: LocationOnChangeData) => {
      const mergedFilters = { ...searchFilters, ...newPartialFilters };
      const newFilters = {
        ...mergedFilters,
        centerlat: parseCoordinate(mergedFilters.centerlat),
        centerlng: parseCoordinate(mergedFilters.centerlng),
      };

      setSearchFilters(newFilters);
      emitAnalyticsEvent(new UpdateLocation({}));
    },
    [searchFilters]
  );

  const searchLink = useMemo(() => {
    return getTrainerSearchPageUrl(searchFilters);
  }, [searchFilters]);

  return (
    <Flex flexDirection="column">
      <Box mt="6x" sx={{ fontSize: theme.text['200'].fontSize }}>
        {/* TODO (DEV-104195): Translate below text before we deploy our service globally */}
        {showSearchFilters && (
          <Location
            label="What's your address?"
            onChange={(data) => {
              // LocationOnChangeData is really what Location.onChange passes. This issue
              // is well documented by the Location component internals.
              // @ts-expect-error
              onLocationChange(data);
            }}
            onClear={onLocationClear}
            location={searchFilters.location}
            locationInputId="landing-search-form-location"
          />
        )}
      </Box>
      <Button
        variant="primary"
        size="large"
        fullWidth
        mt="4x"
        href={searchLink}
        onPress={(): void => {
          emitSearchFormCtaAnalytics({
            serviceTypeSlug,
          });
        }}
      >
        {/* TODO (DEV-104195): Translate below text before we deploy our service globally */}
        {upliftedContent && !puppy ? 'Find trusted dog trainers' : 'Search'}
      </Button>
    </Flex>
  );
};

export default DogTrainingLandingSearchForm;
